import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ScriptEditor } from './story/ScriptEditor';
import { FilmSlateIcon, FolderOpenIcon, CaretRightIcon, CircleNotchIcon, CloudIcon, DownloadIcon, ClockCounterClockwiseIcon, FloppyDiskIcon, CaretLeftIcon, FolderIcon, ListBulletsIcon, ChartBarIcon, CardsIcon } from '@phosphor-icons/react';
import { ProjectService } from '../lib/projectService';
import { Project } from '../types';
import { useToast } from '../context/ToastContext';
import { useProject } from '../context/ProjectContext';
import { useProjectPermissions } from '../hooks/useProjectPermissions';
import { useAuth } from '../context/AuthContext';
import { Button } from "./ui/button";
import { ProductionBar, ProductionBarDivider } from './ProductionBar';

export const StoryPage: React.FC = () => {
    const { user } = useAuth();
    const { showToast } = useToast();
    const navigate = useNavigate();
    const location = useLocation();
    const { selectedProjectId, setSelectedProjectId } = useProject();
    const permissions = useProjectPermissions(selectedProjectId || undefined);
    const [projects, setProjects] = useState<Project[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const projectsPerPage = 10;

    const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'error'>('saved');
    // Editor view state for the bar's active styles (Index Cards is reported by the editor)
    const [showIndexCards, setShowIndexCards] = useState(false);

    // Ref for the scrolling container
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    // Ref for script editor actions
    const editorRef = useRef<any>(null);

    // Initial Load
    useEffect(() => {
        const load = async () => {
            // Get ID from URL query params
            const getInitialId = () => {
                const params = new URLSearchParams(location.search);
                return params.get('project') || undefined;
            };

            let initialId = getInitialId();
            const list = await ProjectService.getProjects();

            setProjects(list);

            if (initialId) {
                setSelectedProjectId(initialId);
            } else if (selectedProjectId && list.some(p => p.id === selectedProjectId)) {
                // Use project from context if valid - update URL
                navigate(`/story?project=${selectedProjectId}`, { replace: true });
            }
            // Auto-select removed to show Project Selector UI
            setIsLoading(false);
        };
        load();
    }, []);

    // Scroll Persistence & Restore logic would go here...

    const selectedProject = projects.find(p => p.id === selectedProjectId);

    const handleProjectSelect = (projectId: string) => {
        setSelectedProjectId(projectId);
        navigate(`/story?project=${projectId}`, { replace: true });
    };

    // Render Project Selector if no project selected
    if (!selectedProjectId) {
        // Calculate pagination
        const totalPages = Math.ceil(projects.length / projectsPerPage);
        const startIndex = (currentPage - 1) * projectsPerPage;
        const endIndex = startIndex + projectsPerPage;
        const currentProjects = projects.slice(startIndex, endIndex);

        return (
            <div className="flex-1 w-full flex flex-col bg-carbon text-white">
                <ProductionBar page="story" />
                <div className="flex-1 flex items-center justify-center px-6">
                    <div className="max-w-2xl w-full">
                        <div className="text-center mb-8 animate-in fade-in slide-in-from-top-4 duration-500">
                            <h1 className="text-4xl font-bold mb-4">Select a Project</h1>
                            <p className="text-white/60 text-lg">
                                Choose a project to write your story in.
                            </p>
                        </div>

                        <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-700">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                {currentProjects.map(project => (
                                    <button
                                        key={project.id}
                                        onClick={() => handleProjectSelect(project.id)}
                                        className="group relative bg-white/5 border border-white/10 rounded-lg p-6 hover:border-violet-500/50 hover:bg-white/10 transition-all text-left"
                                    >
                                        <div className="flex items-start gap-3 mb-2">
                                            <FolderIcon className="w-5 h-5 text-violet-400 flex-shrink-0 mt-1" />
                                            <div className="flex-1 min-w-0">
                                                <h3 className="font-medium text-white truncate group-hover:text-violet-300 transition-colors">
                                                    {project.title}
                                                </h3>
                                                <p className="text-xs text-white/50 mt-1">
                                                    {project.project_type || 'General'}
                                                </p>
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>

                            {/* Pagination Controls */}
                            {totalPages > 1 && (
                                <div className="flex items-center justify-center gap-4 mb-4">
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                        disabled={currentPage === 1}
                                        className="p-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                                    >
                                        <CaretLeftIcon className="w-5 h-5" />
                                    </button>
                                    <span className="text-sm text-white/60">
                                        Page {currentPage} of {totalPages}
                                    </span>
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                        disabled={currentPage === totalPages}
                                        className="p-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                                    >
                                        <CaretRightIcon className="w-5 h-5" />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            ref={scrollContainerRef}
            className="flex-1 w-full bg-carbon text-zinc-100 overflow-y-auto flex flex-col"
        >
            <ProductionBar page="story">
                {/* Status Indicator */}
                <div className="flex items-center gap-1.5">
                    {saveStatus === 'saving' ? (
                        <>
                            <CircleNotchIcon className="w-3 h-3 animate-spin text-zinc-500" />
                            <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-medium">Saving...</span>
                        </>
                    ) : saveStatus === 'error' ? (
                        <span className="text-[10px] uppercase tracking-wider text-red-500 font-medium">Save Failed</span>
                    ) : (
                        <>
                            <CloudIcon className="w-3.5 h-3.5 text-zinc-500" />
                            <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-medium">Saved</span>
                        </>
                    )}
                </div>

                <ProductionBarDivider />

                {/* Editor Actions */}
                <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-zinc-400 hover:text-white hover:bg-white/10 transition-colors"
                    onClick={() => editorRef.current?.exportPDF()}
                    title="Export to PDF"
                >
                    <DownloadIcon className="w-3.5 h-3.5" />
                </Button>

                <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-zinc-400 hover:text-white hover:bg-white/10 transition-colors"
                    onClick={() => editorRef.current?.toggleHistory()}
                    title="Version History"
                >
                    <ClockCounterClockwiseIcon className="w-3.5 h-3.5" />
                </Button>

                <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-zinc-400 hover:text-white hover:bg-white/10 transition-colors"
                    onClick={() => editorRef.current?.saveVersion()}
                    title="Save Version Snapshot"
                >
                    <FloppyDiskIcon className="w-3.5 h-3.5" />
                </Button>

                <ProductionBarDivider />

                {/* View Options */}
                <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-zinc-400 hover:text-white hover:bg-white/10 transition-colors"
                    onClick={() => editorRef.current?.toggleSceneNavigator()}
                    title="Scene Navigator"
                >
                    <ListBulletsIcon className="w-3.5 h-3.5" />
                </Button>

                <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-zinc-400 hover:text-white hover:bg-white/10 transition-colors"
                    onClick={() => editorRef.current?.toggleStats()}
                    title="Script Statistics"
                >
                    <ChartBarIcon className="w-3.5 h-3.5" />
                </Button>

                <Button
                    variant="ghost"
                    size="sm"
                    className={`h-7 w-7 p-0 transition-colors ${showIndexCards ? 'text-indigo-400 bg-indigo-500/20' : 'text-zinc-400 hover:text-white hover:bg-white/10'}`}
                    onClick={() => editorRef.current?.toggleIndexCards()}
                    title={showIndexCards ? 'Back to the screenplay' : 'Index Cards: one card per scene'}
                    aria-pressed={showIndexCards}
                >
                    <CardsIcon className="w-3.5 h-3.5" />
                </Button>
            </ProductionBar>
            {selectedProjectId ? (
                <ScriptEditor ref={editorRef} projectId={selectedProjectId} accountId={user?.id} onSaveStatusChange={setSaveStatus}
                    onViewModeChange={(mode) => setShowIndexCards(mode === 'cards')}
                    readOnly={permissions.isLoading || !permissions.canEdit} />
            ) : (
                <div className="flex flex-col items-center justify-center h-[70vh] text-center max-w-lg mx-auto p-6">
                    <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center mb-6 ring-1 ring-white/10">
                        <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center">
                            <FilmSlateIcon className="w-4 h-4 text-zinc-400" />
                        </div>
                    </div>
                    <h2 className="text-xl font-medium text-zinc-200 mb-2">Ready to write?</h2>
                    <p className="text-zinc-500 mb-8 text-sm">
                        Select an existing story or create a new one to begin your masterpiece.
                    </p>
                    <Button
                        onClick={() => navigate('/projects')}
                        className="bg-white md:hover:bg-zinc-200 text-black font-medium"
                    >
                        <FolderOpenIcon className="w-4 h-4 mr-2" />
                        Browse Projects
                    </Button>
                </div>
            )}
        </div>

    );
};
